// src/components/Timeline.tsx
// Security events timeline with icons and timestamps.

import { cn } from '@core/ui';

export interface TimelineEvent {
  id: string;
  title: string;
  description?: string;
  time: string;
  icon?: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'error';
}

interface TimelineProps {
  events: TimelineEvent[];
  className?: string;
}

const variantColors: Record<string, string> = {
  default: 'bg-muted text-muted-foreground',
  success: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  warning: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

export function Timeline({ events, className }: TimelineProps) {
  return (
    <div className={cn('relative space-y-0', className)}>
      {events.map((event, i) => (
        <div key={event.id} className="relative flex gap-3 pb-6 last:pb-0">
          {i < events.length - 1 && (
            <div className="absolute left-4 top-8 bottom-0 w-px bg-border" />
          )}
          <div className={cn('relative z-10 flex items-center justify-center w-8 h-8 rounded-full shrink-0', variantColors[event.variant ?? 'default'])}>
            {event.icon ?? (
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <circle cx="10" cy="10" r="3" />
              </svg>
            )}
          </div>
          <div className="flex-1 min-w-0 pt-0.5">
            <p className="text-sm font-medium text-foreground">{event.title}</p>
            {event.description && <p className="text-xs text-muted-foreground mt-0.5">{event.description}</p>}
            <p className="text-xs text-muted-foreground mt-1">{event.time}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
